"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import { Search } from "lucide-react"

interface City {
  slug: string
  name: string
  state: string
}

interface Props {
  cities: City[]
  tradeSlug: string
  tradeLabel: string
  currentLocationSlug: string
}

export default function CitySearchFilter({ cities, tradeSlug, tradeLabel, currentLocationSlug }: Props) {
  const [query, setQuery] = useState("")

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cities.slice(0, 24) // show top 24 by default
    return cities.filter(
      (c) => c.name.toLowerCase().includes(q) || c.state.toLowerCase().includes(q)
    ).slice(0, 40)
  }, [query, cities])

  const plural = tradeLabel.endsWith("s") ? tradeLabel : `${tradeLabel}s`

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search cities for ${plural.toLowerCase()}…`}
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
          style={{ fontSize: "16px" }} // prevent iOS zoom
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No cities match &ldquo;{query}&rdquo;</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {filtered.map((city) => (
            <Link
              key={city.slug}
              href={`/directory/${tradeSlug}/${city.slug}`}
              className="text-sm bg-white border border-gray-200 hover:border-amber-400 hover:text-amber-700 text-gray-600 px-3 py-1.5 rounded-full transition-colors"
            >
              {city.name}, {city.state}
            </Link>
          ))}
        </div>
      )}

      {/* Show count when filtering */}
      {!query && cities.length > 24 && (
        <p className="text-xs text-gray-400">
          Showing 24 of {cities.length} cities — search to find yours
        </p>
      )}
      {query && filtered.length > 0 && (
        <p className="text-xs text-gray-400">
          {filtered.length} {filtered.length === 1 ? "city" : "cities"} found
        </p>
      )}
    </div>
  )
}
